export const ADMIN_SETTING_ROLES = ["O", "SUO", "WL"];
export const MY_SETTING_ROLES = ["SA", "A", "SUA", "SS", "S", "M"];

export const isAdminSettingRole = (name?: string) =>
  ADMIN_SETTING_ROLES.includes(name || "");

export const canBypassAgMaintenance = (name?: string) =>
  isAdminSettingRole(name);

export const isMySettingRole = (name?: string) =>
  MY_SETTING_ROLES.includes(name || "");

/** 1/true = on, 0/false = off. Missing uses defaultOn. */
export const isFlagOn = (value: any, defaultOn = false) => {
  if (value === undefined || value === null) return defaultOn;
  return value !== 0 && value !== false;
};

/** Whitelabel admin-setting unlocks. Missing or 0 stays hidden until Privileges turns them on. */
export const WL_ADMIN_FLAGS = [
  "as_change_password",
  "as_website",
  "as_concurrent",
  "as_p2p",
  "as_surveillance",
  "as_white_limit",
  "as_match_bets",
  "as_messages",
  "as_user_settings",
  "as_cheat_bets",
] as const;

export const PRIVILEGE_LABELS: Record<string, string> = {
  result_option: "Result",
  common_setting: "Common Setting",
  as_change_password: "WL Change Password",
  as_website: "WL Website Setting",
  as_concurrent: "WL Concurrent Users",
  as_p2p: "WL P2P Setting",
  as_surveillance: "WL Surveillance",
  as_white_limit: "WL White Label Limit",
  as_match_bets: "WL Match And Bets",
  as_messages: "WL Other Messages",
  as_user_settings: "WL User Settings",
  as_cheat_bets: "WL Cheat Bets",
};

const explicitOn = (value: any) => value === 1 || value === true;
const explicitOff = (value: any) => value === 0 || value === false;

/** Result menu. On for sadmin (O) unless turned off. Other roles stay off until result_option is 1. */
export const canSeeResult = (role: any) => {
  if (!role) return false;
  if (explicitOn(role.result_option)) return true;
  if (explicitOff(role.result_option)) return false;
  return role.name === "O";
};

/** Common Setting block. Sub owner, whitelabel, and every layer under whitelabel stay off until common_setting is 1. */
export const canSeeCommonSetting = (role: any) => {
  if (!role?.name) return false;
  if (role.name === "SUO" || role.name === "WL" || isMySettingRole(role.name)) {
    return explicitOn(role.common_setting);
  }
  if (explicitOff(role.common_setting)) return false;
  return true;
};

/** Non-whitelabel roles are unchanged. Whitelabel needs the matching unlock flag. */
export const wlUnlock = (role: any, flag: string) => {
  if (role?.name !== "WL") return true;
  return explicitOn(role?.[flag]);
};

export const wlAnyUnlock = (role: any, flags: string[]) => {
  if (role?.name !== "WL") return true;
  return flags.some((flag) => explicitOn(role?.[flag]));
};

/** PrivateRoute denies only exact 0. 1 allows. */
export const settingAccess = (
  role: any,
  existing: any,
  opts?: { wlFlag?: string; wlFlags?: string[]; common?: boolean; result?: boolean }
) => {
  if (opts?.result && !canSeeResult(role)) return 0;
  if (opts?.common && !canSeeCommonSetting(role)) return 0;
  if (role?.name === "WL" && (opts?.wlFlag || opts?.wlFlags?.length)) {
    if (opts?.wlFlags?.length) return wlAnyUnlock(role, opts.wlFlags) ? 1 : 0;
    return wlUnlock(role, opts.wlFlag || "") ? 1 : 0;
  }
  if (existing === 0 || existing === false) return 0;
  return 1;
};

/** Fill privilege toggles that are not stored yet so Privileges can turn them on. */
export const applyPrivilegeDefaults = (role: any) => {
  if (!role?.name) return role || {};
  const next = { ...role };
  if (next.result_option == null) next.result_option = next.name === "O" ? 1 : 0;
  if (
    (next.name === "O" ||
      next.name === "SUO" ||
      next.name === "WL" ||
      isMySettingRole(next.name)) &&
    next.common_setting == null
  ) {
    next.common_setting = next.name === "O" ? 1 : 0;
  }
  if (next.name === "WL") {
    WL_ADMIN_FLAGS.forEach((key) => {
      if (next[key] == null) next[key] = 0;
    });
  }
  return next;
};

const SETTING_ACCESS_FLAGS = [
  "add_balance",
  "manage_website",
  "all_website_setting",
  "default_settings",
  "surveillance",
  "whiteLablesCasinoLimit",
  "p2pSettings",
  "sports_main_market",
  "bet_list",
  "fancy_history",
  "manage_fancy",
  "match_history",
  "manage_premium",
  "premium_history",
  "casino_manage",
  "manage_dashboard_images",
  "b2c_contact_seting",
  "banner",
  "change_pssword",
  "concurrent_user",
  "inactive_users",
  "bet_locked_users",
  "deleted_users",
  "cheat_bets",
] as const;

const DEFAULT_ON_FLAGS = new Set([
  "default_settings",
  "change_pssword",
  "concurrent_user",
  "inactive_users",
  "bet_locked_users",
  "deleted_users",
  "cheat_bets",
]);

export const checkSettingOption = (role: any) => {
  if (role == null) return 1;
  if (!role.name) return 0;
  const hasAccess = SETTING_ACCESS_FLAGS.some((key) =>
    isFlagOn(role[key], DEFAULT_ON_FLAGS.has(key))
  );
  return hasAccess ? 1 : 0;
};
